import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './ReferALeadModal.css';

const DEFAULT_FORM_VALUES = {
  name: '',
  mobile: '',
  city: 'Gurgaon',
  intent: 'Buy',
  property_description: '',
  note: '',
};

const INTENT_OPTIONS = [
  { value: 'Buy', label: 'Buy' },
  { value: 'Sell', label: 'Sell' },
  { value: 'Invest', label: 'Invest' },
];

const RECAPTCHA_SITE_KEY = '6LceOhgrAAAAAH3hqfTMsjHMgtqNpWCFAM3lmXFD';

export default function ReferALeadModal({ onClose, onSuccess }) {
  const { authFetch } = useAuth();
  const [isSuccess, setIsSuccess] = useState(false);
  const [overlayError, setOverlayError] = useState('');
  const [submittedData, setSubmittedData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm({ defaultValues: DEFAULT_FORM_VALUES });

  useEffect(() => {
    // Lock scroll on open
    document.body.classList.add('modal-open');

    gtag('event', 'open_refer_a_lead_modal', {
      event_category: 'Referral',
      event_label: 'Modal Opened',
    });

    return () => {
      // Cleanup scroll lock on close
      document.body.classList.remove('modal-open');
    };
  }, []);

  useEffect(() => {
    if (overlayError) {
      const timer = setTimeout(() => setOverlayError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [overlayError]);

  const onInvalid = (formErrors) => {
    const messages = Object.values(formErrors).map(err => err.message);
    setOverlayError(messages.join(' | '));
  };

  const onSubmit = async (data) => {
    setOverlayError('');

    try {
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        throw new Error('reCAPTCHA is not ready. Please try again.');
      }

      window.grecaptcha.ready(async () => {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'refer_lead' });

        const payload = {
          ...data,
          recaptcha_token: token,
        };

        const res = await authFetch(`${API_BASE_URL}/api/refer-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await res.json();
        console.log("API Response:", result);

        if (result.success) {
          setSubmittedData(data);
          onSuccess?.();
          setIsSuccess(true);

          gtag('event', 'submit_referral_lead', {
            event_category: 'Referral',
            event_label: data?.mobile || 'N/A',
            value: 1,
          });
        } else {
          setOverlayError(result.detail || result.message || 'Error submitting referral lead.');
        }
      });
    } catch (err) {
      console.error('Submission error:', err);
      setOverlayError(err.message || 'Failed to submit referral. Please check your connection and try again.');
    }
  };

  const handleReferMore = () => {
    gtag('event', 'refer_more_click', {
      event_category: 'Referral',
      event_label: submittedData?.mobile || 'Unknown',
    });

    reset();
    setIsSuccess(false);
    setOverlayError('');
    setSubmittedData(null);
  };

  const handleClose = () => {
    gtag('event', 'close_after_success', {
      event_category: 'Referral',
      event_label: submittedData?.mobile || 'Unknown',
    });
    document.body.classList.remove('modal-open'); // Ensure scroll lock is removed
    onClose();
  };
  

  return (
    <div className="refer-a-lead-modal-overlay">
      <div className="refer-a-lead-modal">
        <button
          type="button"
          className="modal-close-btn"
          onClick={onClose}
          aria-label="Close Modal"
        >
          &times;
        </button>

        {!isSuccess ? (
          <>
            <h1 className="refer-a-lead-title">Refer & Earn Rewards</h1>
            <p className="excite-message">
              Help your friends find the perfect property while you earn exciting rewards. Your referral can make a difference—join our community of savvy referrers today!
            </p>
            <p className="refer-a-lead-subtext">
              Please provide the details of your referral in the form below.
            </p>
            <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="refer-a-lead-form">
              <div className="form-row-grid">
                <div className="form-field">
                  <label>
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Full name"
                    {...register('name', {
                      required: 'Name is required.',
                      minLength: { value: 2, message: 'Minimum 2 characters' }
                    })}
                  />
                </div>
                <div className="form-field">
                  <label>
                    Mobile <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="E.g. 9810467541"
                    {...register('mobile', {
                      required: 'Mobile is required.',
                      pattern: {
                        value: /^[6-9]\d{9}$/,
                        message: 'Enter a valid 10-digit mobile number'
                      }
                    })}
                  />
                </div>
              </div>

              <div className="form-row-grid">
                <div className="form-field">
                  <label>
                    City <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="City"
                    defaultValue="Gurgaon"
                    {...register('city', { required: 'City is required.' })}
                  />
                </div>
                <div className="form-field intent-field">
                  <label>Intent</label>
                  <select {...register('intent')}>
                    {INTENT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label>
                  Property Description <span className="required">*</span>
                </label>
                <textarea
                  placeholder="E.g., Looking for 3BHK, budget 1Cr"
                  {...register('property_description', {
                    required: 'Property Description is required.',
                    maxLength: { value: 100, message: 'Max 100 characters' }
                  })}
                />
              </div>

              <div className="form-row">
                <label>Additional Note (Optional)</label>
                <textarea
                  placeholder="Add any additional details"
                  {...register('note', {
                    maxLength: { value: 300, message: 'Max 300 characters' }
                  })}
                />
              </div>
              
              {overlayError && <div className="error-toast">{overlayError}</div>}

              <div className="form-actions">
                <button type="button" onClick={handleClose} className="cancel-btn">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="submit-btn">
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="success-content">
            <h2 className="success-title">🎉 Thank You!</h2>
            <p className="success-message">
              {submittedData?.name
                ? `${submittedData.name}'s referral has been submitted successfully.`
                : 'Your referral has been submitted successfully.'}
            </p>
            <p className="success-subtext">✅ Our team will verify the lead and update its status.</p>
            <p className="success-subtext">💰 You’ll earn rewards in 2 steps: <strong>on site visit</strong> and <strong>on deal closure</strong>.</p>
            <p className="success-subtext">We’re on it. Sit back, relax, and keep referring property leads to earn more!</p>
            <div className="success-actions">
              <button onClick={handleReferMore} className="refer-more-btn">Refer More</button>
              <button onClick={handleClose} className="close-btn">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ReferALeadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
